import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { I18nModule, RoutingService } from '@spartacus/core';
import { OrderFacade } from '@spartacus/order/root';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { CheckoutFormService } from 'src/app/core/checkout/facade/checkout-form.service';
import { CUSTOM_LAUNCH_CALLER } from 'src/app/dialog/custom-launch-dialog-config';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { ComponentService } from 'src/app/service/component.service';
import { GoogleTagManagerService } from 'src/app/service/googletagmanager/google-tag-manager.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { PaymentService } from 'src/app/service/payment/payment.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { CheckoutServiceExtends } from '../../../../service/checkout/checkout.service';
import { CheckoutPlaceOrderComponent } from '../checkout-place-order/checkout-place-order.component';

@Component({
  selector: 'app-snappay-place-order',
  standalone: true,
  imports: [CommonModule, I18nModule],
  templateUrl: './snappay-place-order.component.html',
  styleUrls: ['./snappay-place-order.component.scss', '../checkout-place-order/checkout-place-order.component.scss'],
  providers: [CheckoutFormService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnappayPlaceOrderComponent extends CheckoutPlaceOrderComponent implements OnInit, OnDestroy {
  buttonKey: string;

  // Subscriptions
  paymentModeValueChangeSubscription$: Subscription;

  constructor(
    checkoutFormService: CheckoutFormService,
    routingService: RoutingService,
    orderFacade: OrderFacade,
    checkoutServiceExtends: CheckoutServiceExtends,
    cartService: ActiveCartFacade,
    cdr: ChangeDetectorRef,
    componentService: ComponentService,
    baseStoreService: BaseStoreService,
    router: Router,
    gtmService: GoogleTagManagerService,
    storageService: StorageService,
    private modalService: StandardModalService,
    private el: ElementRef,
    private vcr: ViewContainerRef,
    private paymentService: PaymentService
  ) {
    super(
      checkoutFormService,
      routingService,
      orderFacade,
      checkoutServiceExtends,
      cartService,
      cdr,
      componentService,
      baseStoreService,
      router,
      gtmService,
      storageService
    );
  }

  ngOnInit(): void {
    this.placeOrderBtnSubscription$ = this.componentService.checkoutFormsError$.subscribe((checkoutFormsError) => {
      if (checkoutFormsError) {
        this.isDisabled = Object.values(checkoutFormsError)?.some((hasError) => hasError);
        this.cdr.detectChanges();
      }
    });
    this.paymentModeValueChangeSubscription$ = this.paymentModeValueChanges().subscribe();
  }

  onClick(): void {
    this.checkoutFormService.checkoutForms
      .pipe(
        take(1),
        map((data) => data.formsObjectValue)
      )
      .subscribe((formsObjectValue: any) => {
        switch (formsObjectValue.paymentMode.checkoutPaymentType) {
          case 'SHIMANO_PG':
            this.openSnappayIframe(formsObjectValue);
            break;
          default:
            this.placeOrder();
            break;
        }
      });
  }

  openSnappayIframe(formsObjectValue: any): void {
    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.IFRAME_CONTAINER, this.el, this.vcr, {
        modalType: 'snappay',
        src: this.paymentService.getPaymentSession(this.buildPlaceOrderPayload(formsObjectValue)).pipe(
          map((res: any) => {
            // build snappay url
            const session = ShimanoUtilMethod.decodeBase64ToObject(res.payloadResponse);
            return `${session.baseUrl}/Interop/InteropRequest?reqno=${session.requestId}`;
          })
        ),
      })
      .subscribe();
  }

  paymentModeValueChanges(): Observable<any> {
    return this.checkoutFormService.checkoutForms.pipe(
      map((data) => data.formsObjectValue.paymentMode),
      distinctUntilChanged(),
      filter(Boolean),
      tap((paymentMode: any) => {
        this.buttonKey = paymentMode.code === '>' ? 'checkoutReview.enterCard' : 'checkoutReview.placeOrder';
      })
    );
  }

  ngOnDestroy(): void {
    this.placeOrderBtnSubscription$?.unsubscribe();
    this.paymentModeValueChangeSubscription$?.unsubscribe();
  }
}
